import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Typography,
  message,
  Empty,
  Input,
  Select,
  Progress,
  Tooltip,
} from 'antd';
import { InfoCircleOutlined, SyncOutlined } from '@ant-design/icons';

import DataSourceTable from '../components/DataSourceTable';
import DataSourceModal from '../components/DataSourceModal';
import SearchBar from '../../../components/commonComponents/searchBar';
import { getFileType, getStatus, storageUsagePercentage } from '../helpers';
import {
  listDataSources,
  showDataSource,
  showLearningDocument,
  usageMetrics,
} from '../../../reduxToolkit/services/corpus.service';
import {
  selectDataSources,
  selectCorpusLoading,
  selectCorpusError,
  selectCorpusCreating,
  selectCorpusDeletingIds,
  selectCorpusUpdatingIds,
  selectCorpusSyncingIds,
  selectCurrentDataSource,
  setCurrentDataSource,
  selectCorpusUsageMetrics,
  selectLearningDocument,
} from '../../../reduxToolkit/corpusSlice';
import { LOADING_STATUS, SORT_DEFINITIONS } from '../constants/corpusPage';
import ButtonGX from '../../channels/page/webChat/ButtonGX';

const { Title, Text } = Typography;

import animationData from '../components/lottie/DataAnalytics.json';
import PaperAnimation from '../components/lottie/PaperAirplane.json';
import MagnifyData from '../components/lottie/MagnifyData.json';
import Lottie from 'lottie-react';

const Corpus = () => {
  const dispatch = useDispatch();
  const inputRefContainer = useRef();
  // Local search input state controlling the query string applied to the table.
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Ant Design message API instance for inline toast notifications.
  const [messageApi, contextHolder] = message.useMessage();
  // Tracks the active modal view (e.g. upload, link) or null when closed.
  const [modalView, setModalView] = useState(null);
  // Controls sort order applied to list queries.
  const [sortOrder, setSortOrder] = useState('desc');
  const [fileUploading, setFileUploading] = useState(false);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchValue.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [searchValue]);
  // Pagination config mirrored with the table component to manage page/size.
  const [paginationState, setPaginationState] = useState({
    current: 1,
    pageSize: 10,
  });

  // Redux selectors for fetching datasource entities and request statuses.
  const dataSources = useSelector(selectDataSources);
  const loading = useSelector(selectCorpusLoading);
  const creating = useSelector(selectCorpusCreating);
  const error = useSelector(selectCorpusError);
  const deletingIds = useSelector(selectCorpusDeletingIds);
  const updatingIds = useSelector(selectCorpusUpdatingIds);
  const syncingIds = useSelector(selectCorpusSyncingIds);
  const currentDataSource = useSelector(selectCurrentDataSource);
  const corpusUsageMetrics = useSelector(selectCorpusUsageMetrics);

  const { currentBrand } = useSelector((state) => state.app);

  const meta = useSelector((state) => state.corpus?.meta);
  const totalCount = meta?.count ?? dataSources.length;

  const sortOptions = useMemo(
    () =>
      SORT_DEFINITIONS.map(({ value, label, Icon, iconClass, textClass }) => ({
        value,
        label,
        className: 'flex items-center gap-2',
        meta: {
          Icon,
          iconClass,
          textClass,
        },
      })),
    [],
  );

  const selectedSortDefinition = useMemo(
    () =>
      SORT_DEFINITIONS.find((item) => item.value === sortOrder) ??
      SORT_DEFINITIONS[0],
    [sortOrder],
  );

  // Fetch datasources using the latest pagination and search parameters.
  const fetchDataSources = ({
    page = paginationState.current,
    limit = paginationState.pageSize,
    search = debouncedSearch,
    sort = sortOrder,
    sortField,
    sortOrder: sorterOrder,
  } = {}) => {
    if (!currentBrand?.brand_id) return;

    // Initialize params with common fields
    const params = {
      page,
      limit,
      search: search?.trim() || undefined,
    };

    // Handle sorting
    console.log({ sortField, sortOrder });

    if (sortField && sorterOrder) {
      const order = sorterOrder === 'descend' ? 'desc' : 'asc';

      if (sortField === 'fileSize') {
        // For fileSize, use file_size parameter
        params.file_size = order;
      } else if (sortField === 'createdAt') {
        // For createdAt, use sort parameter
        params.sort = order;
      } else {
        // For other fields, use the field name as sort parameter
        params.sort = order === 'desc' ? `-${sortField}` : sortField;
      }
    }
    dispatch(
      listDataSources({
        brandId: currentBrand?.brand_id,
        params,
      }),
    );
  };

  useEffect(() => {
    if (currentBrand?.brand_id) {
      fetchDataSources({ page: 1, limit: paginationState.pageSize });
    }
  }, [currentBrand?.brand_id]);

  // useEffect(() => {
  //   if (currentBrand?.brand_id) {
  //     dispatch(usageMetrics({ brandId: currentBrand?.brand_id }));
  //   }
  // }, [currentBrand?.brand_id]);

  useEffect(() => {
    if (!currentDataSource && dataSources?.length) {
      const firstWithId = dataSources.find((item) => item?.uuid || item?.id);
      dispatch(
        setCurrentDataSource(
          firstWithId?.uuid ??
            firstWithId?.id ??
            dataSources[0]?.uuid ??
            dataSources[0]?.id,
        ),
      );
    }
  }, [currentDataSource, dataSources, dispatch]);

  const tableData = useMemo(
    () =>
      dataSources?.map((item, index) => ({
        id: item?.uuid ?? item?.id ?? `data-source-${index}`,
        displayName: item?.display_name || 'Untitled File',
        fileDetails: {
          type: item?.ds_file_type,
          details: item?.data_source_details,
          file_content_type:
            item?.data_source_details?.gcs_file_details?.content_type,
          ...getFileType(
            item?.ds_file_type,
            item?.data_source_details?.url,
            item?.display_name,
          ),
        },
        status: getStatus(item?.status),
        updatedAt: item?.updated_at || item?.updatedAt,
        createdAt: item?.created_at || item?.createdAt,
        fileSize: item?.file_size,
        raw: item,
      })) || [],
    [dataSources],
  );

  // Apply search term and reset pagination
  const handleSearch = (value) => {
    setSearchValue(value);
    setPaginationState((prev) => ({
      ...prev,
      current: 1,
    }));
  };

  // Effect to trigger search when debounced search value changes
  useEffect(() => {
    if (debouncedSearch !== undefined) {
      fetchDataSources({
        page: paginationState.current,
        limit: paginationState.pageSize,
        search: debouncedSearch,
        sortField: paginationState.sortField,
        sortOrder: paginationState.sortOrder,
      });
    }
  }, [
    debouncedSearch,
    sortOrder,
    paginationState.sortField,
    paginationState.sortOrder,
    paginationState.current,
    paginationState.pageSize,
  ]);

  // Trigger a single datasource sync request and surface success feedback.
  const handleSync = (id) => {
    dispatch(showDataSource({ id, brandId: currentBrand?.brand_id }));
    messageApi.success('Datasource refreshed.');
  };

  // Handle bulk toolbar actions such as refreshing or syncing all datasources.
  const handleMoreActions = ({ key }) => {
    switch (key) {
      case 'refresh':
        fetchDataSources();
        break;
      case 'sync-all':
        tableData.forEach((item) =>
          dispatch(
            showDataSource({ id: item.id, brandId: currentBrand?.brand_id }),
          ),
        );
        messageApi.success('Datasources refreshed.');
        break;
      default:
        messageApi.info('Action coming soon.');
    }
  };

  // Close the datasource modal and clear any selected view.
  const handleCloseModal = () => {
    if (creating) return;
    setModalView(null);
  };

  // Switch modal view or default to upload when opening.
  const handleModalViewChange = (view) => {
    setModalView(view ?? 'upload');
  };

  const getLearningDocument = async () => {
    const response = await dispatch(showLearningDocument({ brandId }));
    console.log({ response });
    if (response?.payload?.learning_document == 0) {
      for (const item of dataSources) {
        if (LOADING_STATUS.includes(item?.status_value)) {
          dispatch(showDataSource({ id: item.uuid, brandId }));
        }
      }
    }
    return response;
  };

  const learningDocument = useSelector(selectLearningDocument);
  const brandId = currentBrand?.brand_id;
  const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);

  // Polling effect
  useEffect(() => {
    if (!brandId) return;

    // Set loading animation based on learning document count
    setShowLoadingAnimation(learningDocument > 0);

    // Set up polling interval if there are learning documents
    const intervalId =
      learningDocument > 0
        ? setInterval(() => getLearningDocument(), 3000)
        : null;

    // Initial fetch
    getLearningDocument();

    // Cleanup
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [brandId, learningDocument, dispatch]);

  return (
    <div className="h-dvh overflow-scroll bg-gray-100 px-3 pb-6 py-10 rounded-lg">
      {contextHolder}
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
        <div className="flex items-center justify-between">
          <div className="min-w-max">
            <Title level={3} className="!m-0 !text-[#1F2937]">
              Data sources
            </Title>
            <Text type="secondary" className="text-sm">
              Manage your sources
            </Text>
          </div>
          <div className="w-1/2">
            <div className="flex flex-col- gap-2">
              <Text className="max-w-min min-w-[110px]">Credit Usage</Text>
              <div className="flex-1">
                <Progress
                  percent={corpusUsageMetrics?.credit_usage || 0}
                  strokeColor={{
                    '0%': '#87d068',
                    '100%': '#e9101071',
                  }}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <Text className="text-sm">Storage limit</Text>
                <Tooltip
                  color="white"
                  title={
                    <div>
                      <Text className="text-black">
                        Used{' '}
                        {
                          storageUsagePercentage(
                            corpusUsageMetrics?.storage_usage,
                          ).current
                        }{' '}
                        of{' '}
                        {
                          storageUsagePercentage(
                            corpusUsageMetrics?.storage_usage,
                          ).total
                        }
                      </Text>
                    </div>
                  }
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
              <div className="flex-1">
                <div>
                  <Progress
                    percent={
                      storageUsagePercentage(corpusUsageMetrics?.storage_usage)
                        .percentage
                    }
                    strokeColor={{
                      '0%': '#87d068',
                      '100%': '#e9101071',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            showLoadingAnimation ? 'h-[300px] opacity-100' : 'h-0 opacity-0'
          }`}
        >
          {showLoadingAnimation && (
            <Lottie
              style={{ height: '300px' }}
              animationData={MagnifyData}
              loop={true}
              autoplay={true}
            />
          )}
        </div>
        <DataSourceModal
          googleDriveMessage={() => {
            messageApi.info('Google Drive integration coming soon.');
          }} // remove this once drive integration is over
          open={Boolean(modalView)}
          view={modalView ?? 'upload'}
          onClose={handleCloseModal}
          setFileUploading={setFileUploading}
          fileUploading={fileUploading}
          onChangeView={handleModalViewChange}
        />
        <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <SearchBar
                ref={inputRefContainer}
                searchKey={searchValue}
                setSearchKey={setSearchValue}
                onChange={handleSearch}
                expandable={false}
                placeholder="Search datasources"
              />
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
                <Button
                  size="middle"
                  icon={<SyncOutlined />}
                  className="w-full md:w-auto"
                  onClick={() => handleMoreActions({ key: 'refresh' })}
                >
                  Refresh
                </Button>
                <ButtonGX
                  disabled={learningDocument > 0}
                  onClick={handleModalViewChange}
                >
                  Add Source
                </ButtonGX>
              </div>
            </div>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                {typeof error === 'string'
                  ? error
                  : 'Unable to load datasources.'}
              </div>
            )}
            {tableData.length || loading ? (
              <DataSourceTable
                loading={loading}
                data={tableData}
                onSync={handleSync}
                syncingIds={syncingIds}
                deletingIds={deletingIds}
                updatingIds={updatingIds}
                onTableChange={(pagination, filters, sorter) => {
                  // Only handle sort changes here
                  const { field, order } = sorter;

                  setPaginationState((prev) => ({
                    ...prev,
                    current: 1, // Always reset to first page on sort
                    sortField: field,
                    sortOrder: order,
                  }));

                  // No need to fetch here - the state update will trigger the effect below
                }}
                pagination={{
                  current: paginationState.current,
                  pageSize: paginationState.pageSize,
                  total: totalCount,
                  onChange: (page, pageSize) => {
                    setPaginationState((prev) => ({
                      ...prev,
                      current: page,
                      pageSize,
                    }));
                    // No need to fetch here - the state update will trigger the effect below
                  },
                  onShowSizeChange: (_, size) => {
                    setPaginationState((prev) => ({
                      ...prev,
                      current: 1,
                      pageSize: size,
                    }));
                    // No need to fetch here - the state update will trigger the effect below
                  },
                }}
              />
            ) : (
              <div className="py-12">
                <Empty description="No datasources found" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Corpus;
